<template>
  <div class="flex flex-col gap-2">
    <ui-label v-if="label">{{ label }}</ui-label>
    <div class="flex flex-wrap gap-2">
      <money-input
        ref="moneyInput"
        class="min-w-40 flex-1"
        :model-value="modelValue"
        :currency="currencyCode"
        :disabled="disabled"
        @update:model-value="$emit('update:modelValue', $event)"
        @blur="$emit('blur')"
      />
      <select-field
        v-if="showPeriod"
        trigger-class="w-44"
        :model-value="period"
        :options="budgetPeriods"
        :reduce="(obj) => obj.machine_name"
        :aria-label="tr('Budget')"
        :disabled="periodDisabled"
        @update:model-value="$emit('update:period', $event)"
      />
    </div>

    <slot name="after-input" />

    <slot
      name="info-texts"
      :invalid-budget="invalidBudget"
      :invalid-budget-info-text="invalidBudgetInfoText"
      :is-budget-request-running="isBudgetRequestRunning"
      :min-budget-info-text="minBudgetInfoText"
      :min-budget="minBudget"
    >
      <transition-expand>
        <info-text v-if="invalidBudget" type="error">
          <span v-html="invalidBudgetInfoText" />
        </info-text>
        <info-text v-else :icon="isBudgetRequestRunning ? 'loader' : null">
          {{ minBudgetInfoText }}
        </info-text>
      </transition-expand>
    </slot>

    <transition-expand>
      <info-text v-if="showAvailableAds && availableAdsFromBudget > 0">
        <template v-if="availableAdsFromBudget !== 1">
          {{ tr('With this budget, you will be able to run :num ads simultaneously.', { ':num': availableAdsFromBudget }) }}
        </template>
        <template v-else>
          {{ tr('With this budget, you will be able to run 1 ad simultaneously.') }}
        </template>
      </info-text>
    </transition-expand>
  </div>
</template>

<script>
import BudgetInfoMixin from '@/mixins/BudgetInfo.js';
import InfoText from '@/ui/components/InfoText.vue';
import MoneyInput from '@/ui/components/MoneyInput.vue';
import SelectField from '@/ui/components/SelectField.vue';
import TransitionExpand from '@/ui/components/TransitionExpand.vue';
import debounce from '@/utils/debounce.js';

export default {
  name: 'BudgetEditor',
  components: {
    InfoText,
    MoneyInput,
    SelectField,
    TransitionExpand,
  },
  mixins: [ BudgetInfoMixin ],
  props: {
    modelValue: {
      type: [ Number, String ],
      default: null,
    },
    period: {
      type: String,
      default: 'daily',
    },
    level: {
      type: String,
      required: true,
      validator: val => [ 'campaign', 'adset' ].includes(val),
    },
    currencyCode: {
      type: String,
      default: 'EUR',
    },
    adAccountCurrencySymbol: {
      type: String,
      default: null,
    },
    adAccountId: {
      type: String,
      default: null,
    },
    minDailyBudget: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    periodDisabled: {
      type: Boolean,
      default: false,
    },
    showAvailableAds: {
      type: Boolean,
      default: false,
    },
    showPeriod: {
      type: Boolean,
      default: true,
    },
    label: {
      type: String,
      default: null,
    },
    lifetimeStartTime: {
      type: [ Date, String ],
      default: null,
    },
    lifetimeEndTime: {
      type: [ Date, String ],
      default: null,
    },
    lifetimeLifespanMaxDays: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      debouncedGetMinBudget: debounce(this.getMinBudget, 500),
    };
  },
  computed: {
    budgetPeriods() {
      return this.$store.getters['app/budgetPeriods'];
    },
    budget() {
      return this.modelValue;
    },
    budgetPeriod() {
      return this.period;
    },
    budgetLevel() {
      return this.level;
    },
    budgetAdAccountId() {
      return this.adAccountId;
    },
    budgetCurrencySymbol() {
      return this.adAccountCurrencySymbol;
    },
    budgetLifetimeStartTime() {
      return this.lifetimeStartTime;
    },
    budgetLifetimeEndTime() {
      return this.lifetimeEndTime;
    },
    budgetLifetimeLifespanMaxDays() {
      return this.lifetimeLifespanMaxDays;
    },
    availableAdsFromBudget() {
      if (!this.showAvailableAds || this.budgetPeriod !== 'daily') {
        return 0;
      }
      if (this.minDailyBudget && this.budget >= this.minDailyBudget) {
        return Math.floor(this.budget / this.minDailyBudget);
      }
      return 0;
    },
  },
  watch: {
    period() {
      this.debouncedGetMinBudget();
    },
    adAccountId(val) {
      if (val) {
        this.getMinBudget();
      }
    },
    lifetimeStartTime(val) {
      if (val) {
        this.debouncedGetMinBudget();
      }
    },
    lifetimeEndTime(val) {
      if (val) {
        this.debouncedGetMinBudget();
      }
    },
    lifetimeLifespanMaxDays() {
      this.debouncedGetMinBudget();
    },
  },
  created() {
    this.getMinBudget();
  },
  methods: {
    focus() {
      this.$nextTick(() => {
        const moneyInputComponent = this.$refs.moneyInput;
        if (!moneyInputComponent || !moneyInputComponent.$el) {
          return;
        }

        const input = moneyInputComponent.$el.tagName === 'INPUT'
          ? moneyInputComponent.$el
          : moneyInputComponent.$el.querySelector('input');

        if (input) {
          input.focus();
          input.select();
        }
      });
    },
  },
};
</script>
